import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, FolderOpen, Wrench, Clock, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSearch, useRecentSearches, SearchResult } from "@/hooks/useSearch";
import { useDebouncedValue } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";

// Strip HTML tags from text for display
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export function HeaderSearch() {
  const [query, setQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results = [], isLoading } = useSearch(debouncedQuery);
  const { recentSearches, addRecentSearch, clearRecentSearches } = useRecentSearches();

  // Group results by content type
  const blogResults = results.filter(r => r.content_type === 'blog');
  const projectResults = results.filter(r => r.content_type === 'project');
  const webtoolResults = results.filter(r => r.content_type === 'webtool');

  const hasResults = results.length > 0;
  const showRecent = query.length < 2 && recentSearches.length > 0;
  const showDropdown = isExpanded && (query.length > 0 || showRecent);

  // All selectable items for keyboard navigation
  const allItems: (SearchResult | { type: 'recent'; query: string })[] = [
    ...(showRecent ? recentSearches.map(q => ({ type: 'recent' as const, query: q })) : []),
    ...results,
  ];

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedQuery, showRecent]);

  const handleSelect = useCallback((result: SearchResult) => {
    const path = result.content_type === 'blog'
      ? `/blog/${result.slug}`
      : result.content_type === 'project'
      ? `/project/${result.slug}`
      : `/webtools/${result.slug}`;

    addRecentSearch(query);
    navigate(path);
    setIsExpanded(false);
    setQuery("");
  }, [query, navigate, addRecentSearch]);

  const handleRecentSearch = useCallback((recentQuery: string) => {
    setQuery(recentQuery);
  }, []);

  const handleViewAllResults = useCallback(() => {
    if (query.trim()) {
      addRecentSearch(query);
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setIsExpanded(false);
      setQuery("");
    }
  }, [query, navigate, addRecentSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      setQuery("");
      return;
    }

    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && selectedIndex < allItems.length) {
        const item = allItems[selectedIndex];
        if ('type' in item && item.type === 'recent') {
          handleRecentSearch(item.query);
        } else {
          handleSelect(item as SearchResult);
        }
      } else if (query.trim()) {
        handleViewAllResults();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    }
  }, [selectedIndex, allItems, query, handleSelect, handleRecentSearch, handleViewAllResults]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'blog': return <FileText className="h-4 w-4 text-type-blog" />;
      case 'project': return <FolderOpen className="h-4 w-4 text-type-project" />;
      case 'webtool': return <Wrench className="h-4 w-4 text-type-webtool" />;
      default: return null;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Collapsed: Just the search icon */}
      {!isExpanded && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(true)}
          aria-label="Search"
        >
          <Search className="h-[18px] w-[18px]" />
        </Button>
      )}

      {/* Expanded: Search input */}
      {isExpanded && (
        <div className="relative animate-in fade-in-0 slide-in-from-right-2 duration-200 mx-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9 pr-8 h-9 w-64 bg-background border-input"
          />
          <button
            type="button"
            onClick={() => {
              setIsExpanded(false);
              setQuery("");
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg z-50 animate-in fade-in-0 zoom-in-95">
          {/* Loading State */}
          {isLoading && debouncedQuery.length >= 2 && (
            <div className="flex items-center justify-center gap-2 py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Searching...</span>
            </div>
          )}

          {/* No Results */}
          {!isLoading && debouncedQuery.length >= 2 && !hasResults && (
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground">No results for "{debouncedQuery}"</p>
              <p className="text-xs text-muted-foreground mt-1">Try different keywords</p>
            </div>
          )}

          {/* Minimum Characters Hint */}
          {query.length > 0 && query.length < 2 && !showRecent && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters
            </div>
          )}

          {/* Recent Searches */}
          {showRecent && (
            <div className="py-2">
              <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Recent Searches
              </div>
              {recentSearches.map((recentQuery, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearch(recentQuery)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                    selectedIndex === index && "bg-accent"
                  )}
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{recentQuery}</span>
                </button>
              ))}
              <div className="px-3 py-1.5 border-t border-border mt-1">
                <button
                  onClick={clearRecentSearches}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear recent searches
                </button>
              </div>
            </div>
          )}

          {/* Search Results */}
          {hasResults && (
            <div className="py-2">
              {/* Blog Posts */}
              {blogResults.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Blog Posts
                  </div>
                  {blogResults.slice(0, 3).map((result, idx) => {
                    const itemIndex = (showRecent ? recentSearches.length : 0) + idx;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          "w-full flex items-start gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                          selectedIndex === itemIndex && "bg-accent"
                        )}
                      >
                        {getIcon('blog')}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.title}</div>
                          {result.excerpt && (
                            <div className="text-xs text-muted-foreground truncate">
                              {stripHtml(result.excerpt).substring(0, 60)}...
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Projects */}
              {projectResults.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Projects
                  </div>
                  {projectResults.slice(0, 3).map((result, idx) => {
                    const itemIndex = (showRecent ? recentSearches.length : 0) + blogResults.slice(0, 3).length + idx;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          "w-full flex items-start gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                          selectedIndex === itemIndex && "bg-accent"
                        )}
                      >
                        {getIcon('project')}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.title}</div>
                          {result.excerpt && (
                            <div className="text-xs text-muted-foreground truncate">
                              {stripHtml(result.excerpt).substring(0, 60)}...
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Webtools */}
              {webtoolResults.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Webtools
                  </div>
                  {webtoolResults.slice(0, 3).map((result, idx) => {
                    const itemIndex = (showRecent ? recentSearches.length : 0) + blogResults.slice(0, 3).length + projectResults.slice(0, 3).length + idx;
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          "w-full flex items-start gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left",
                          selectedIndex === itemIndex && "bg-accent"
                        )}
                      >
                        {getIcon('webtool')}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{result.title}</div>
                          {result.excerpt && (
                            <div className="text-xs text-muted-foreground truncate">
                              {stripHtml(result.excerpt).substring(0, 60)}...
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* View All Results */}
              {results.length >= 5 && (
                <div className="px-3 py-2 border-t border-border mt-1">
                  <button
                    onClick={handleViewAllResults}
                    className="w-full text-sm text-center text-primary hover:underline"
                  >
                    View all {results.length} results →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty state when focused but no query and no recent */}
          {query.length === 0 && !showRecent && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Start typing to search
            </div>
          )}
        </div>
      )}
    </div>
  );
}
